/** @format */
import {useState} from 'react';

import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {Box, Button, LinearProgress, Typography} from '@mui/material';
import {clsx} from 'clsx';
import {Trans} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import Alert from '@mui/material/Alert';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import SidebarEdit from 'components/MediaplanSidebar/SidebarEdit';
import SidebarNew from 'components/MediaplanSidebar/SidebarNew';

import BackToProjectButton from 'components/BackToProjectButton';

import {AccountsCompanyMediaplanType} from 'graphql/generated/types';

import {classes} from './constants';
import {
  useGetMediaplanTypeQuery,
  useUpdateCompanyMediaplanTypeMutation,
} from './graphql/__generated__';
import {StyledBox} from './styles';

const Type = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const isEdit = searchParams.get('edit');

  const {data, loading, error} = useGetMediaplanTypeQuery({
    variables: {companyId: companyId!},
  });

  const [updateMediaplanType, {error: _updateMediaplanTypeError}] =
    useUpdateCompanyMediaplanTypeMutation();

  const savedMediaplanType = data?.currentUser?.company?.mediaplanType ?? null;
  const isCompanyHasMediaplanType = savedMediaplanType !== null;

  const [selectedMediaplanType, setSelectedMediaplanType] =
    useState<AccountsCompanyMediaplanType | null>(null);
  const mediaplanType = savedMediaplanType ?? selectedMediaplanType;

  const handleTypeChange = (type: AccountsCompanyMediaplanType) => {
    if (isCompanyHasMediaplanType) return;

    setSelectedMediaplanType(type);
  };

  const onSave = async () => {
    if (!isCompanyHasMediaplanType) {
      await updateMediaplanType({
        variables: {
          id: companyId!,
          typeObject: {type: mediaplanType!},
        },
      });
    }

    navigate(`/home/project/periods?companyId=${companyId}`);
  };

  const canDisplayMediaplanType = (checkedType: AccountsCompanyMediaplanType) => {
    if (!isCompanyHasMediaplanType) return true;

    return mediaplanType === checkedType;
  };

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  return (
    <StyledBox>
      {isEdit ? (
        <SidebarEdit activeStep={'TYPE'} enableAll />
      ) : (
        <SidebarNew activeStep={'TYPE'} enableAll />
      )}

      <Box>
        <Typography className={classes.heading}>
          <Trans>Тип кампаний</Trans>
        </Typography>
      </Box>

      {
        <>
          <Box sx={{mt: 8}} data-testid='type'>
            <Typography className={classes.description}>
              <Trans>Выберите тип кампании</Trans>
            </Typography>
          </Box>
          <Box sx={{mt: 2}}>
            <Typography className={classes.description}>
              <Trans>
                Этот выбор определит, какие кампании попадут из кабинета в проект, а также повлияет
                на список доступных KPI в медиаплане
              </Trans>
            </Typography>
          </Box>
          <Box className={classes.alertBlock}>
            <PriorityHighIcon className={classes.alertBlockIcon} />
            <Typography className={classes.description}>
              <Trans>
                Тип кампании закрепляется за проектом навсегда. Чтобы анализировать оба типа
                кампаний, создайте два проекта
              </Trans>
            </Typography>
          </Box>
          <Box className={classes.controlsWrapper}>
            {canDisplayMediaplanType('PERFORM') && (
              <Button
                className={clsx(
                  classes.typeButton,
                  mediaplanType === 'PERFORM' && classes.typeButtonActive,
                )}
                onClick={() => handleTypeChange('PERFORM')}
              >
                <Typography className={classes.typeButtonHeading}>
                  <Trans>За клики</Trans>
                </Typography>
                <Typography className={classes.typeButtonDescription}>
                  <Trans>Перформ-кампании</Trans>
                </Typography>
              </Button>
            )}

            {canDisplayMediaplanType('MEDIA') && (
              <Button
                className={clsx(
                  classes.typeButton,
                  mediaplanType === 'MEDIA' && classes.typeButtonActive,
                )}
                onClick={() => handleTypeChange('MEDIA')}
              >
                <Typography className={classes.typeButtonHeading}>
                  <Trans>За показы</Trans>
                </Typography>
                <Typography className={classes.typeButtonDescription}>
                  <Trans>Медийные кампании</Trans>
                </Typography>
              </Button>
            )}
          </Box>
          {mediaplanType && (
            <Box
              sx={{
                mt: 24,
                mb: 24,
              }}
            >
              <PrimaryMediumButton onClick={onSave}>
                <Trans>Сохранить и перейти к периодам</Trans>
              </PrimaryMediumButton>
              {isEdit && <BackToProjectButton />}
            </Box>
          )}
        </>
      }
    </StyledBox>
  );
};

export default Type;
